import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const DB_NAME = 'report-image-store';
const DB_VERSION = 1;
const STORE_NAME = 'images';

const supportsIndexedDb = () => typeof indexedDB !== 'undefined';

const openDb = () =>
  new Promise((resolve, reject) => {
    if (!supportsIndexedDb()) {
      resolve(null);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const getAllRecords = async () => {
  const db = await openDb();
  if (!db) return [];

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

const putRecord = async record => {
  const db = await openDb();
  if (!db) return;

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.put(record);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

const deleteRecord = async id => {
  const db = await openDb();
  if (!db) return;

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

const clearStore = async () => {
  const db = await openDb();
  if (!db) return;

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

const makeId = () => {
  if (globalThis?.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }
  return `img_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const ReportImageContext = createContext(null);

export const ReportImageProvider = ({ children }) => {
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const objectUrlsRef = useRef(new Map());

  const attachPreviewUrl = useCallback(record => {
    let previewUrl = objectUrlsRef.current.get(record.id);
    if (!previewUrl && record.blob) {
      previewUrl = URL.createObjectURL(record.blob);
      objectUrlsRef.current.set(record.id, previewUrl);
    }

    return {
      ...record,
      previewUrl: previewUrl || null,
    };
  }, []);

  const refreshImages = useCallback(async () => {
    setIsLoading(true);
    try {
      const records = await getAllRecords();
      setImages(records.map(attachPreviewUrl));
    } catch {
      setImages([]);
    } finally {
      setIsLoading(false);
    }
  }, [attachPreviewUrl]);

  useEffect(() => {
    refreshImages();
    return () => {
      objectUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
      objectUrlsRef.current.clear();
    };
  }, [refreshImages]);

  const addImage = useCallback(
    async ({ blob, filename, meta }) => {
      if (!blob) return null;
      const record = {
        id: makeId(),
        blob,
        filename: filename || 'capture.png',
        meta: meta || {},
        createdAt: new Date().toISOString(),
      };

      try {
        await putRecord(record);
      } catch {
        // ignore persistence failures
      }

      setImages(prev => [...prev, attachPreviewUrl(record)]);
      return record.id;
    },
    [attachPreviewUrl]
  );

  const removeImage = useCallback(async id => {
    try {
      await deleteRecord(id);
    } catch {
      // ignore persistence failures
    }

    setImages(prev => prev.filter(item => item.id !== id));

    const previewUrl = objectUrlsRef.current.get(id);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      objectUrlsRef.current.delete(id);
    }
  }, []);

  const clearImagesForStudy = useCallback(
    async studyId => {
      if (!studyId) return;
      const toRemove = images.filter(item => item.meta?.studyId === studyId);

      await Promise.all(toRemove.map(item => removeImage(item.id)));
    },
    [images, removeImage]
  );

  const clearAllImages = useCallback(async () => {
    try {
      await clearStore();
    } catch {
      // ignore persistence failures
    }

    objectUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    objectUrlsRef.current.clear();
    setImages([]);
  }, []);

  const getImagesForStudy = useCallback(
    studyId => images.filter(item => item.meta?.studyId === studyId),
    [images]
  );

  const value = useMemo(
    () => ({
      images,
      isLoading,
      addImage,
      removeImage,
      clearImagesForStudy,
      clearAllImages,
      refreshImages,
      getImagesForStudy,
    }),
    [
      images,
      isLoading,
      addImage,
      removeImage,
      clearImagesForStudy,
      clearAllImages,
      refreshImages,
      getImagesForStudy,
    ]
  );

  return <ReportImageContext.Provider value={value}>{children}</ReportImageContext.Provider>;
};

export const useReportImages = () => {
  const ctx = useContext(ReportImageContext);
  if (!ctx) {
    throw new Error('useReportImages must be used within ReportImageProvider');
  }
  return ctx;
};
